import { useEffect, useSyncExternalStore } from "react";
import { Store } from "../helpers/store";
import { ParamsWatcher } from "../helpers/params";

type Register<K extends string> = [
    storeKey: K,
    addrStr: string,
    updatePeriodSec?: number,
    priority?: "low" | "normal" | "high",
    registersNum?: number,
    transform?: (v: number[]) => string,
];

export default function useWatchDeviceRegistersRawData<K extends string>(
    store: Store<string>,
    params: ParamsWatcher,
    networkId: number,
    slaveId: number,
    settings: Readonly<Register<K>>[],
    rereadOnError?: boolean,
): { [key in K]: string | undefined } {
    useEffect(() => {
        for (const [
            storeKey,
            addrStr,
            updatePeriodSec = 5,
            priority = "normal",
            registersNum = 1,
            transform = (v: number[]) => v[0].toFixed(),
        ] of settings) {
            const addr = parseInt(addrStr, 10);
            if (isNaN(addr)) {
                throw new Error(`Invalid addr: ${addrStr}`);
            }
            const kind = addrStr.slice(-1);
            params.watchRawData({
                paramKeyStr: storeKey,
                networkId,
                slaveId,
                addr,
                kind:
                    kind === "i"
                        ? "input"
                        : kind === "c"
                        ? "coil"
                        : kind === "d"
                        ? "discrete-input"
                        : "holding",
                registersNum,
                transform,
                updatePeriodMs: updatePeriodSec && updatePeriodSec * 1000,
                priority,
                rereadOnError,
            });
        }
    }, []);

    return useSyncExternalStore(store.subscribe, store.getSnapshot) as {
        [key in K]: string | undefined;
    };
}
